import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { AnnouncementBar } from "../components/layout/AnnouncementBar";
import { Header } from "../components/layout/Header";
import { Footer } from "../components/layout/Footer";
import { ShopProductCard } from "../components/shop/ShopProductCard";
import { CollectionBanner } from "../components/shop/CollectionBanner";
import { Overline } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { Seo, breadcrumbLd, itemListLd } from "../components/Seo";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES as FALLBACK_CATEGORIES, CATEGORY_LABEL, COLLECTION_COPY } from "../data/products";
import { fetchCategories, fetchProducts, fetchProductTypes } from "../services/api";
import { formatINR } from "../data/content";
import { SHOP } from "../constants/testIds/shop";

const PAGE_SIZE = 24;
const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "newest", label: "Newest" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-desc", label: "Price: High to Low" },
];
const FINISHES = [
  { id: "gold", label: "Gold-tone" },
  { id: "rose-gold", label: "Rose gold-tone" },
  { id: "silver", label: "Silver-tone" },
];
const HIGHLIGHTS = [
  { id: "newArrival", label: "New arrivals" },
  { id: "bestseller", label: "Bestsellers" },
];

const FilterSection = ({ title, children }) => (
  <div className="border-b border-brand-sand py-6">
    <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-charcoal">
      <span className="h-px w-4 bg-brand-gold/70" aria-hidden="true" />
      {title}
    </p>
    <div className="mt-4">{children}</div>
  </div>
);

const FiltersPanel = ({ categories, selectedCats, toggleCategory, types, selectedTypes, toggleType, selectedFinishes, toggleFinish, selectedFlags, toggleFlag, inStock, setInStock, price, setPrice, bounds, onClear }) => (
  <div>
    <div className="flex items-center justify-between">
      <p className="font-serif text-xl text-brand-charcoal">Refine</p>
      <button type="button" onClick={onClear} data-testid={SHOP.clearFilters} className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold underline-offset-4 hover:underline">
        Clear all
      </button>
    </div>
    <FilterSection title="Category">
      <div className="space-y-3" data-testid={SHOP.categoryFilter}>
        {categories.map((c) => (
          <label key={c.slug} className="flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
            <Checkbox checked={selectedCats.includes(c.slug)} onCheckedChange={() => toggleCategory(c.slug)} data-testid={`shop-filter-category-${c.slug}`} />
            {c.name}
          </label>
        ))}
      </div>
    </FilterSection>
    {types.length > 0 && (
      <FilterSection title="Type">
        <div className="space-y-3" data-testid="shop-filter-type">
          {types.map((t) => (
            <label key={t.slug} className="flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
              <Checkbox checked={selectedTypes.includes(t.slug)} onCheckedChange={() => toggleType(t.slug)} data-testid={`shop-filter-type-${t.slug}`} />
              {t.name}
            </label>
          ))}
        </div>
      </FilterSection>
    )}
    <FilterSection title="Finish">
      <div className="space-y-3" data-testid="shop-filter-finish">
        {FINISHES.map((f) => (
          <label key={f.id} className="flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
            <Checkbox checked={selectedFinishes.includes(f.id)} onCheckedChange={() => toggleFinish(f.id)} data-testid={`shop-filter-finish-${f.id}`} />
            {f.label}
          </label>
        ))}
      </div>
    </FilterSection>
    <FilterSection title="Highlights">
      <div className="space-y-3" data-testid={SHOP.flagFilter}>
        {HIGHLIGHTS.map((h) => (
          <label key={h.id} className="flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
            <Checkbox checked={selectedFlags.includes(h.id)} onCheckedChange={() => toggleFlag(h.id)} data-testid={`shop-filter-flag-${h.id}`} />
            {h.label}
          </label>
        ))}
      </div>
    </FilterSection>
    <FilterSection title="Price">
      <div data-testid={SHOP.priceSlider}>
        <Slider min={bounds.min} max={bounds.max} step={50} value={price} onValueChange={setPrice} className="mt-2" />
        <div className="mt-4 flex items-center justify-between text-xs text-brand-stone">
          <span>{formatINR(bounds.min)}</span>
          <span className="font-semibold text-brand-charcoal" data-testid="shop-price-value">Up to {formatINR(price[0])}</span>
        </div>
      </div>
    </FilterSection>
    <FilterSection title="Availability">
      <label className="flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
        <Checkbox checked={inStock} onCheckedChange={setInStock} data-testid={SHOP.inStockFilter} />
        In stock only
      </label>
    </FilterSection>
  </div>
);

const Pagination = ({ page, pages, onPage }) => {
  if (pages <= 1) return null;
  const nums = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(pages, page + 2); i += 1) nums.push(i);
  const btn = "min-w-[40px] border px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors disabled:opacity-40";
  return (
    <nav className="mt-14 flex flex-wrap items-center justify-center gap-2" aria-label="Pagination" data-testid="shop-pagination">
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="shop-page-prev" className={`${btn} border-brand-sand text-brand-charcoal hover:border-brand-gold hover:text-brand-gold`}>Prev</button>
      {nums.map((n) => (
        <button key={n} type="button" onClick={() => onPage(n)} aria-current={n === page ? "page" : undefined} data-testid={`shop-page-${n}`}
          className={`${btn} ${n === page ? "border-brand-charcoal bg-brand-charcoal text-brand-cream" : "border-brand-sand text-brand-charcoal hover:border-brand-gold hover:text-brand-gold"}`}>{n}</button>
      ))}
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="shop-page-next" className={`${btn} border-brand-sand text-brand-charcoal hover:border-brand-gold hover:text-brand-gold`}>Next</button>
    </nav>
  );
};

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES.map((c) => ({ slug: c.id, name: c.label })));
  const [types, setTypes] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, price_range: { min: 0, max: 5000 } });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [priceDraft, setPriceDraft] = useState(null);
  const [qDraft, setQDraft] = useState(searchParams.get("q") || "");
  const searchRef = useRef(null);

  const selectedCats = (searchParams.get("category") || "").split(",").filter(Boolean);
  const selectedTypes = (searchParams.get("type") || "").split(",").filter(Boolean);
  const selectedFinishes = (searchParams.get("finish") || "").split(",").filter(Boolean);
  const selectedFlags = (searchParams.get("flags") || "").split(",").filter(Boolean);
  const inStock = searchParams.get("in_stock") === "1";
  const sort = searchParams.get("sort") || "featured";
  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const q = searchParams.get("q") || "";
  const maxPriceParam = searchParams.get("max_price");
  const bounds = { min: Math.max(0, data.price_range.min || 0), max: Math.max(data.price_range.max || 0, 50) };
  const price = priceDraft || [maxPriceParam ? Number(maxPriceParam) : bounds.max];

  const setParam = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === null || v === "" || v === undefined || v === false) next.delete(k);
      else next.set(k, String(v));
    });
    if (!("page" in updates)) next.delete("page");
    setSearchParams(next);
  };

  useEffect(() => {
    fetchCategories().then((cats) => cats.length && setCategories(cats)).catch(() => {});
    fetchProductTypes().then(setTypes).catch(() => {});
    if (searchParams.get("focus") === "search") searchRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    const params = { page, limit: PAGE_SIZE, sort };
    if (q) params.q = q;
    if (selectedCats.length) params.category = selectedCats.join(",");
    if (selectedTypes.length) params.type = selectedTypes.join(",");
    if (selectedFinishes.length) params.finish = selectedFinishes.join(",");
    if (selectedFlags.length) params.flags = selectedFlags.join(",");
    if (inStock) params.in_stock = true;
    if (maxPriceParam) params.max_price = Number(maxPriceParam);
    fetchProducts(params)
      .then((res) => { setData(res); setLoadError(""); })
      .catch(() => setLoadError("We couldn't load the bracelets right now."))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [searchParams]);

  const toggleList = (key, list) => (id) => setParam({ [key]: (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]).join(",") });
  const clearAll = () => { setPriceDraft(null); setQDraft(""); setSearchParams(new URLSearchParams()); };
  const onPage = (n) => { setParam({ page: n }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const catLabel = useMemo(() => Object.fromEntries(categories.map((c) => [c.slug, c.name])), [categories]);
  const singleCat = selectedCats.length === 1 ? selectedCats[0] : null;
  const heading = singleCat ? catLabel[singleCat] || CATEGORY_LABEL[singleCat] || "Bracelets" : "All Bracelets";
  const collectionCopy = singleCat ? COLLECTION_COPY[singleCat] : null;
  const seoPath = singleCat ? `/shop?category=${singleCat}` : "/shop";

  const filterProps = {
    categories, selectedCats, toggleCategory: toggleList("category", selectedCats),
    types, selectedTypes, toggleType: toggleList("type", selectedTypes),
    selectedFinishes, toggleFinish: toggleList("finish", selectedFinishes),
    selectedFlags, toggleFlag: toggleList("flags", selectedFlags),
    inStock, setInStock: (v) => setParam({ in_stock: v ? "1" : null }),
    price, setPrice: setPriceDraft, bounds, onClear: clearAll,
  };

  const commitPrice = () => { if (priceDraft) { setParam({ max_price: priceDraft[0] >= bounds.max ? null : priceDraft[0] }); setPriceDraft(null); } };

  return (
    <div className="min-h-screen bg-brand-cream font-sans text-brand-charcoal" data-testid={SHOP.page} onPointerUp={commitPrice} onKeyUp={commitPrice}>
      <Seo
        title={`${heading} — Bracelets for a Brighter You`}
        description={`Browse ${heading.toLowerCase()} by HESHAURA. ${data.total} hand-finished bracelets with prices in INR, filters by style, finish and price. Small-batch, made in India.`}
        path={seoPath}
        noindex={page > 1 || !!q}
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Shop Bracelets", path: "/shop" }]), itemListLd(data.items, heading)]}
      />
      <AnnouncementBar />
      <Header />
      <main>
        {singleCat && collectionCopy ? (
          <CollectionBanner slug={singleCat} count={data.total} image={categories.find((c) => c.slug === singleCat)?.image || ""} />
        ) : (
          <section className="relative overflow-hidden border-b border-brand-sand bg-gradient-to-b from-brand-champagne/70 via-brand-cream to-brand-cream">
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
              <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-stone" data-testid="shop-breadcrumb">
                <a href="/" className="transition-colors hover:text-brand-gold">Home</a><span>/</span><span className="text-brand-charcoal">Shop Bracelets</span>
              </nav>
              <div className="mt-5"><Overline>The Bracelet Shop</Overline></div>
              <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-brand-charcoal sm:text-5xl lg:text-6xl" data-testid="shop-heading">
                {heading}
              </h1>
              <div className="mt-6 h-px w-24 bg-gradient-to-r from-brand-gold to-transparent" aria-hidden="true" />
              <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-brand-stone" data-testid="shop-subheading">
                Everyday, minimal, charm, tennis, pearl and festive bracelets — hand-finished in small batches. Filter by what speaks to you.
              </p>
            </div>
          </section>
        )}
        <section className="border-b border-brand-sand bg-brand-surface">
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
            <form
              className="flex max-w-xl items-center border border-brand-sand bg-white shadow-[0_1px_0_rgba(28,25,23,0.03)] transition-colors focus-within:border-brand-gold"
              onSubmit={(e) => { e.preventDefault(); setParam({ q: qDraft.trim() }); }}
              role="search"
            >
              <Search className="ml-4 h-4 w-4 shrink-0 text-brand-stone" />
              <input
                ref={searchRef}
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
                placeholder="Search bracelets by name…"
                aria-label="Search bracelets"
                data-testid="shop-search-input"
                className="w-full bg-transparent px-3 py-3 text-sm outline-none"
              />
              {q && (
                <button type="button" aria-label="Clear search" data-testid="shop-search-clear" onClick={() => { setQDraft(""); setParam({ q: null }); }} className="px-3 text-brand-stone hover:text-brand-gold">
                  <X className="h-4 w-4" />
                </button>
              )}
              <button type="submit" data-testid="shop-search-submit" className="bg-brand-charcoal px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-cream hover:bg-brand-gold">
                Search
              </button>
            </form>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
          <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-12">
            <aside className="hidden lg:block">
              <div className="sticky top-24"><FiltersPanel {...filterProps} /></div>
            </aside>
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-brand-sand pb-5">
                <p className="text-sm text-brand-stone" data-testid={SHOP.resultCount}>
                  {data.total} {data.total === 1 ? "bracelet" : "bracelets"}{q ? ` for “${q}”` : ""}
                  {data.pages > 1 && <span className="ml-2 text-brand-stone/70">· Page {data.page} of {data.pages}</span>}
                </p>
                <div className="flex items-center gap-3">
                  <span className="hidden text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-stone sm:block">Sort</span>
                  <Select value={sort} onValueChange={(v) => setParam({ sort: v === "featured" ? null : v })}>
                    <SelectTrigger className="w-[190px] rounded-none border-brand-sand bg-brand-surface text-sm" data-testid={SHOP.sortSelect}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {SORTS.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`shop-sort-${s.id}`}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-6 lg:hidden">
                <details className="border border-brand-sand bg-brand-surface px-4 py-3">
                  <summary className="cursor-pointer list-none text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-charcoal" data-testid="shop-mobile-filters">Filters</summary>
                  <div className="mt-4"><FiltersPanel {...filterProps} /></div>
                </details>
              </div>

              {loading ? (
                <div className="py-24 text-center" data-testid={SHOP.loading}><p className="font-serif text-2xl text-brand-charcoal">Loading bracelets…</p></div>
              ) : loadError ? (
                <div className="py-24 text-center" data-testid={SHOP.error}>
                  <p className="font-serif text-2xl text-brand-charcoal">{loadError}</p>
                  <button type="button" onClick={load} data-testid={SHOP.retry} className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-brand-gold underline-offset-4 hover:underline">Try again</button>
                </div>
              ) : data.items.length === 0 ? (
                <div className="py-24 text-center" data-testid={SHOP.empty}>
                  <p className="font-serif text-2xl text-brand-charcoal">No bracelets match those filters.</p>
                  <button type="button" onClick={clearAll} className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-brand-gold underline-offset-4 hover:underline">Clear all filters</button>
                </div>
              ) : (
                <>
                  <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-3 lg:gap-x-6" data-testid={SHOP.grid}>
                    {data.items.map((p, i) => (
                      <Reveal key={p.id} delay={(i % 3) * 0.06}><ShopProductCard product={p} /></Reveal>
                    ))}
                  </div>
                  <Pagination page={data.page} pages={data.pages} onPage={onPage} />
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
